import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { overlayContainer } from './mapOverlay.styles';

type MapOverlayProps = {
  map: google.maps.Map;
  position: google.maps.LatLngLiteral;
  /** Pixel offset from the marker center point (default: { x: 0, y: -34 }) */
  offset?: { x: number; y: number };
  children: React.ReactNode;
};

// Google Maps overlay projection works in CSS pixels; keep this geometry numeric.
const MAP_EDGE_PADDING_PX = 8;
const ANCHOR_INSET_PX = 16;
const DEFAULT_OFFSET = { x: 0, y: -34 };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

type OverlayViewConstructorWithHitGuards = typeof google.maps.OverlayView & {
  preventMapHitsAndGesturesFrom?: (element: Element) => void;
  preventMapHitsFrom?: (element: Element) => void;
};

const preventMapInteractionPassthrough = (container: HTMLElement) => {
  const overlayView = google.maps.OverlayView as OverlayViewConstructorWithHitGuards;

  if (overlayView.preventMapHitsAndGesturesFrom) {
    overlayView.preventMapHitsAndGesturesFrom(container);
    return;
  }

  overlayView.preventMapHitsFrom?.(container);
};

/**
 * Renders React children at a geo coordinate on the map using google.maps.OverlayView.
 * The overlay container is added to the floatPane so it renders above markers.
 */
export const MapOverlay = ({ map, position, offset = DEFAULT_OFFSET, children }: MapOverlayProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) {
      containerRef.current = document.createElement('div');
      containerRef.current.className = overlayContainer;
      preventMapInteractionPassthrough(containerRef.current);
    }

    const container = containerRef.current;
    const latLng = new google.maps.LatLng(position.lat, position.lng);

    class Overlay extends google.maps.OverlayView {
      onAdd() {
        const panes = this.getPanes();
        panes?.floatPane.appendChild(container);
        setReady(true);
      }

      draw() {
        const projection = this.getProjection();
        if (!projection) return;

        const divPoint = projection.fromLatLngToDivPixel(latLng);
        const containerPoint = projection.fromLatLngToContainerPixel(latLng);
        if (!divPoint || !containerPoint) return;

        const mapDiv = map.getDiv();
        const mapRect = mapDiv.getBoundingClientRect();
        const mapWidth = mapDiv.clientWidth || mapRect.width;
        const mapHeight = mapDiv.clientHeight || mapRect.height;
        const overlayWidth = container.offsetWidth;
        const overlayHeight = container.offsetHeight;

        if (!mapWidth || !mapHeight || !overlayWidth || !overlayHeight) {
          container.style.left = `${divPoint.x + offset.x}px`;
          container.style.top = `${divPoint.y + offset.y}px`;
          container.style.transform = 'translate(-50%, -100%)';
          return;
        }

        const verticalGap = Math.abs(offset.y);
        const paneOffsetX = divPoint.x - containerPoint.x;
        const paneOffsetY = divPoint.y - containerPoint.y;
        const anchorX = containerPoint.x + offset.x;
        const aboveAnchorY = containerPoint.y - verticalGap;
        const belowAnchorY = containerPoint.y + verticalGap;
        const spaceAbove = aboveAnchorY - MAP_EDGE_PADDING_PX;
        const spaceBelow = mapHeight - MAP_EDGE_PADDING_PX - belowAnchorY;
        const placement = spaceAbove >= overlayHeight || spaceAbove >= spaceBelow ? 'above' : 'below';
        const preferredTop = placement === 'above' ? aboveAnchorY - overlayHeight : belowAnchorY;
        const maxLeft = Math.max(MAP_EDGE_PADDING_PX, mapWidth - overlayWidth - MAP_EDGE_PADDING_PX);
        const maxTop = Math.max(MAP_EDGE_PADDING_PX, mapHeight - overlayHeight - MAP_EDGE_PADDING_PX);
        const left = clamp(anchorX - overlayWidth / 2, MAP_EDGE_PADDING_PX, maxLeft);
        const top = clamp(preferredTop, MAP_EDGE_PADDING_PX, maxTop);
        const maxArrowX = Math.max(ANCHOR_INSET_PX, overlayWidth - ANCHOR_INSET_PX);
        const arrowX = clamp(anchorX - left, ANCHOR_INSET_PX, maxArrowX);

        container.dataset.mapOverlayPlacement = placement;
        container.style.setProperty('--map-overlay-anchor-x', `${arrowX}px`);
        container.style.left = `${left + paneOffsetX}px`;
        container.style.top = `${top + paneOffsetY}px`;
        container.style.transform = 'none';
      }

      onRemove() {
        container.remove();
        setReady(false);
      }
    }

    const overlay = new Overlay();
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, position.lat, position.lng, offset.x, offset.y]);

  useEffect(() => {
    if (!ready || !containerRef.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      overlayRef.current?.draw();
    });
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [ready]);

  useEffect(() => {
    if (!ready || typeof window === 'undefined') return;

    const frame = window.requestAnimationFrame(() => {
      overlayRef.current?.draw();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [ready, children]);

  if (!ready || !containerRef.current) {
    return null;
  }

  return createPortal(children, containerRef.current);
};
